<template>
  <div style="padding-top: 1em;">
    <div v-if="loading" class="cga-status">
      <b-icon icon="spinner" custom-class="fa-spin" />
      <span>{{$t('cgu.loading')}}</span>
    </div>

    <div v-else-if="loadError" class="cga-status">
      {{$t('cgu.error')}}
    </div>

    <div class="home_wrap" v-show="!loading && !loadError">
      <div class="pdf-container">
        <canvas ref="pdfCanvas"></canvas>
      </div>
    </div>

    <div class="controls" v-show="!loading && !loadError">
      <button @click="prevPage" :disabled="pageNum <= 1">{{$t('cgu.previous')}}</button>
      <span> {{$t('cgu.page', { current: pageNum, total: numPages })}} </span>
      <button @click="nextPage" :disabled="pageNum >= numPages">{{$t('cgu.next')}}</button>
    </div>

    <b-field class="controls" v-if="isActive !== null">
      <b-button @click="accepteCGA" :disabled="loading || loadError">{{$t('cgu.accepter')}}</b-button>
      <b-button @click="refuse">{{$t('cgu.refuser')}}</b-button>
    </b-field>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import pdfjsLib from 'pdfjs-dist'

export default {
  data () {
    return {
      isActive: false,
      pdf: null,
      pageNum: 1,
      numPages: 0,
      pdfUrl: '', // URL of the PDF to load
      // L'onglet s'affiche avant que le document soit disponible : ces deux etats portent
      // le temoin d'attente et le message d'echec.
      loading: true,
      loadError: false,
    }
  },
  computed: {
    ...mapState(['user']),
  },
  props: {
    // Chaine (URL) ou binaire : pdf.js accepte les deux, et le contenu arrive
    // desormais en ArrayBuffer.
    documentUrl: [String, ArrayBuffer],

    // Transmis explicitement plutot que deduit de l'URL : la deduction reposait sur
    // la position du segment dans window.location.href, ce qui casse des que la
    // fenetre s'ouvre sans navigation prealable.
    portefeuilleId: [String, Number],
  },
  watch: {
    // Le contenu est demande apres l'affichage : il faut reagir a son arrivee.
    documentUrl (value) {
      if (value) {
        this.open(value)
      }
    },
  },
  mounted () {
    if (this.documentUrl) {
      this.open(this.documentUrl)
    }
  },
  methods: {
    async saveCga (val) {
      const id = Number(this.portefeuilleId)

      // M-Files attend un entier : une valeur non entiere fait echouer la requete avec
      // « Could not convert string to integer ».
      if (!Number.isInteger(id) || id <= 0) {
        console.error('Identifiant de portefeuille invalide :', this.portefeuilleId)
        this.apiError('Portefeuille non identifié, enregistrement impossible.')
        return
      }

      try {
        await this.$store.dispatch('setCgaValue', {
          user: this.user.name,
          id: id,
          val: val
        })
      } catch (e) {
        this.apiError(e)
      }
    },
    accepteCGA () {
      return this.saveCga(true)
    },
    refuse () {
      return this.saveCga(false)
    },
    open (pdfUrl) {
      this.pdfUrl = pdfUrl
      this.isVisible = true
      this.loadPdf(pdfUrl)
    },
    loadPdf (source) {
      // pdf.js accepte une URL ou un { data } : le contenu arrive desormais en
      // binaire, mais les deux formes restent acceptees.
      const src = typeof source === 'string' ? source : { data: source }

      this.loading = true
      this.loadError = false

      pdfjsLib.getDocument(src).promise.then(
        (pdfDoc_) => {
          this.pdf = pdfDoc_
          this.numPages = pdfDoc_.numPages
          this.loading = false
          // Le rendu suit l'affichage du canvas, masque tant que loading vaut vrai.
          this.$nextTick(() => this.renderPage(this.pageNum))
        },
        (error) => {
          console.error('Error loading PDF: ', error)
          this.loading = false
          this.loadError = true
        }
      )
    },
    renderPage (num) {
      this.pdf.getPage(num).then((page) => {
        const canvas = this.$refs.pdfCanvas
        const context = canvas.getContext('2d')
        const viewport = page.getViewport({ scale: 1.5 }) // Adjust scale for zoom

        // Set the canvas dimensions to match the PDF page
        canvas.height = viewport.height
        canvas.width = viewport.width

        // Render the page to the canvas context
        page.render({ canvasContext: context, viewport: viewport })
      })
    },
    nextPage () {
      if (this.pageNum < this.numPages) {
        this.pageNum++
        this.renderPage(this.pageNum)
      }
    },
    prevPage () {
      if (this.pageNum > 1) {
        this.pageNum--
        this.renderPage(this.pageNum)
      }
    },
  },
}
</script>

<style scoped>
.controls{
  padding: 2px;
  max-width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

/* Les boutons de pagination ne portent aucune classe Bulma : sans cette regle, ils
   heritent du "cursor: default" que le navigateur applique aux <button>. Ecartes
   lorsqu'ils sont desactives, en premiere et derniere page.
   Ce bloc de style est en CSS pur : pas d'imbrication ni de commentaire //. */
.controls button:not(:disabled) {
  cursor: pointer;
}
.cga-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6em;
  min-height: 8em;
  padding: 2em;
  text-align: center;
  color: #7f7f7f;
}
</style>
